import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Grid, X, Play, Pause } from 'lucide-react';
import Hero from './sections/Hero';
import Features from './sections/Features';
import ModuleDetails from './sections/ModuleDetails';
import Statistics from './sections/Statistics';
import Technology from './sections/Technology';
import Contact from './sections/Contact';
import '../styles/PresentationMode.css';

interface PresentationModeProps {
  onExit: () => void;
}

const PresentationMode: React.FC<PresentationModeProps> = ({ onExit }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isOverview, setIsOverview] = useState(false);
  const [isAutoPlay, setIsAutoPlay] = useState(false);

  const slides = [
    { component: <Hero />, title: 'Mozmina System', description: 'Complete Mining Operations Control' },
    { component: <Features />, title: 'System Modules', description: 'Four Integrated Modules' },
    { component: <ModuleDetails />, title: 'Module Details', description: 'In-Depth Features' },
    { component: <Statistics />, title: 'Benefits', description: 'System Impact & Value' },
    { component: <Technology />, title: 'Technology', description: 'Cloud Infrastructure' },
    { component: <Contact />, title: 'Contact', description: 'Get Started Today' }
  ];

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsOverview(false);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
          event.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          prevSlide();
          break;
        case 'Escape':
          event.preventDefault();
          if (isOverview) {
            setIsOverview(false);
          } else {
            onExit();
          }
          break;
        case 'Tab':
          event.preventDefault();
          setIsOverview(!isOverview);
          break;
        case 'Home':
          event.preventDefault();
          setCurrentSlide(0);
          break;
        case 'End':
          event.preventDefault();
          setCurrentSlide(slides.length - 1);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, isOverview, onExit, slides.length]);

  // Auto-play functionality
  useEffect(() => {
    let interval: number;
    if (isAutoPlay && !isOverview) {
      interval = window.setInterval(() => {
        nextSlide();
      }, 10000); // 10 seconds per slide
    }
    return () => window.clearInterval(interval);
  }, [isAutoPlay, isOverview, nextSlide]);

  // Touch/swipe gestures
  useEffect(() => {
    let startX = 0;
    let endX = 0;

    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      endX = e.changedTouches[0].clientX;
      const diffX = startX - endX;
      
      if (Math.abs(diffX) > 50) { // Minimum swipe distance
        if (diffX > 0) {
          nextSlide(); // Swipe left = next slide
        } else {
          prevSlide(); // Swipe right = previous slide
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [nextSlide, prevSlide]);

  if (isOverview) {
    return (
      <div className="presentation-overview">
        <div className="overview-header">
          <h2>Presentation Overview</h2>
          <button onClick={() => setIsOverview(false)} className="close-overview">
            <X size={24} />
          </button>
        </div>
        <div className="overview-grid">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`overview-slide ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
            >
              <div className="slide-preview">
                <div className="slide-content">{slide.component}</div>
              </div>
              <div className="slide-info">
                <h3>{slide.title}</h3>
                <p>{slide.description}</p>
                <span className="slide-number">{index + 1}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="presentation-mode">
      {/* Presentation Controls */}
      <div className="presentation-controls">
        <div className="controls-left">
          <button onClick={onExit} className="control-btn exit-btn" title="Exit Presentation (ESC)">
            <X size={20} />
          </button>
          <button 
            onClick={() => setIsOverview(true)} 
            className="control-btn overview-btn" 
            title="Overview (TAB)"
          >
            <Grid size={20} />
          </button>
        </div>

        <div className="controls-center">
          <button onClick={prevSlide} className="control-btn nav-btn" title="Previous (←)">
            <ChevronLeft size={20} />
          </button>
          <span className="slide-counter">
            {currentSlide + 1} / {slides.length}
          </span>
          <button onClick={nextSlide} className="control-btn nav-btn" title="Next (→)">
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="controls-right">
          <button 
            onClick={() => setIsAutoPlay(!isAutoPlay)} 
            className={`control-btn autoplay-btn ${isAutoPlay ? 'active' : ''}`}
            title="Auto-play"
          >
            {isAutoPlay ? <Pause size={20} /> : <Play size={20} />}
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        />
      </div>

      {/* Current Slide */}
      <div className="presentation-slide">
        <div className="slide-container">
          {slides[currentSlide].component}
        </div>
      </div>

      {/* Slide Navigation Dots */}
      <div className="slide-dots">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === currentSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            title={slides[index].title}
          />
        ))}
      </div>

      {/* Help Overlay */}
      <div className="help-overlay">
        <div className="help-shortcuts">
          <span>← → Navigate</span>
          <span>TAB Overview</span>
          <span>ESC Exit</span>
        </div>
      </div>
    </div>
  );
};

export default PresentationMode;